import React, { useState, useEffect, useContext, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormCheck, FormGroup } from 'react-bootstrap';
import { FormContext } from '../FormContext';
import FormInputHelp from './parts/FormInputHelp';
import '../styles/checkbox.scss';

const defaultOptions = ['Y', 'N'];

const FormInputCheckbox = props => {
  const { controlId, disabled, field, noLabel, value } = props;
  const { ee } = useContext(FormContext);
  const firstUpdate = useRef(true);

  let initialOptions = field.options || defaultOptions;
  if (!Array.isArray(props.options) || props.options.length > 2) {
    console.warn('Invalid checkbox options', props.options);
    initialOptions = defaultOptions;
  }

  const initialChecked = value === initialOptions[0];
  const [options] = useState(initialOptions);
  const [checked, setChecked] = useState(initialChecked);

  const handleUpdate = newValue => {
    if (newValue !== undefined) {
      setChecked(newValue === options[0]);
    }
  };

  const handleEvent = ({ action, payload }) => {
    if (action === 'update') {
      handleUpdate(payload.value);
    }
  };

  useEffect(() => {
    ee.on(controlId, handleEvent);

    return () => {
      ee.off(controlId);
    };
  });

  useEffect(() => {
    if (!options.some(v => v === value)) {
      console.warn(`New checkbox value: ${value} is invalid`);
      return;
    }

    const current = options[checked ? 0 : 1];

    if (current !== value) {
      setChecked(!checked);
    }
  }, [value]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const current = options[checked ? 0 : 1];

    // Send to Parent handleChange method
    props.onChange(field.name, current, () => {
      props.onBlur(field.name);
    });
  }, [checked]);

  const isDisabled = field.readOnly || disabled;
  const label = noLabel ? null : field.label;

  const containerClass = classNames('checkbox-container', {
    'is-readonly': disabled,
  });

  return (
    <FormGroup controlId={controlId} className={containerClass} key={controlId}>
      <FormCheck inline>
        <FormCheck.Input
          type="checkbox"
          id={`${controlId}-control`}
          onChange={e => setChecked(e.target.checked)}
          onClick={() => props.onClick(field.name)}
          disabled={isDisabled}
          checked={checked}
        />
        <FormCheck.Label htmlFor={`${controlId}-control`}>{label}</FormCheck.Label>
      </FormCheck>
      <FormInputHelp>{field.helpText}</FormInputHelp>
    </FormGroup>
  );
};

FormInputCheckbox.defaultProps = {
  field: {},
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  noLabel: false,
};

FormInputCheckbox.propTypes = {
  controlId: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.object,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  noLabel: PropTypes.bool,
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default FormInputCheckbox;
